import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, FakeText, IconButton, TextField } from '@totalsoft/rocket-ui';
import { Checkbox, FormControlLabel } from '@mui/material';
import { Save, ViewList } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { templateNameVar } from '../variables';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { SAVE_GENERIC_FILTER_TEMPLATE_GENERAL_DATA } from '../mutations';
import { GENERIC_FILTER_TEMPLATE_GENERAL_DATA_SAVED } from '../subscriptions';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';
import { generalDataValidator } from 'features/genericFilterTemplates/validators';
import { GENERIC_FILTER_TEMPLATE_GENERAL_DATA_QUERY } from '../queries';
import { useToast } from 'hooks/toasts/useToast';
import FlexBox from 'components/common/FlexBox';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_GENERIC_FILTER_TEMPLATES_WRITE } from 'constants/rightsCodes';

const GenericFilterTemplateGeneralDataComponent = ({ id }) => {
  const historyContext = useHistoryWithContext();
  const addToast = useToast();
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_GENERIC_FILTER_TEMPLATES_WRITE);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens();
  const [saving, setSaving] = useState(false);

  const [validation, validate] = useDirtyFieldValidation(generalDataValidator);

  // #region Query

  const { loading, data } = useQuery(GENERIC_FILTER_TEMPLATE_GENERAL_DATA_QUERY, {
    variables: {
      id
    },
    skip: !id
  });

  const generalData = useMemo(() => {
    if (!data || !data.genericFilterTemplateGeneralData) return null;
    return data.genericFilterTemplateGeneralData;
  }, [data]);

  // #endregion

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  // #region Side effects

  useEffect(() => {
    resetLens(generalData);
  }, [generalData, resetLens]);

  useEffect(() => {
    validate(get(lens), dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  useEffect(() => {
    templateNameVar(get(lens.name));
  }, [lens]);

  // #endregion

  // #region Save general data

  const [saveGeneralData] = useMutation(SAVE_GENERIC_FILTER_TEMPLATE_GENERAL_DATA, {
    onError: () => {
      setSaving(false);
    }
  });

  const handleSave = useCallback(() => {
    const generalData = get(lens);
    if (!validate(generalData)) return;
    setSaving(true);
    saveGeneralData({ variables: { input: generalData } });
  }, [lens, validate, saveGeneralData]);

  useSubscription(GENERIC_FILTER_TEMPLATE_GENERAL_DATA_SAVED, {
    onData: ({
      data: {
        data: { genericFilterTemplateGeneralDataSaved }
      }
    }) => {
      addToast(t('GenericFilterTemplate.Saved'), 'success');
      // Is new template?
      if (!generalData?.id) {
        // Reset lens to enable navigation without prompt
        resetLens();
        historyContext.push(`/genericFilterTemplates/${genericFilterTemplateGeneralDataSaved.data.id}`);
        return;
      }
      setSaving(false);
      resetLens(genericFilterTemplateGeneralDataSaved.data);
    }
  });

  // #endregion

  return loading ? (
    <Card>
      <FakeText lines={5} />
    </Card>
  ) : (
    <Card
      icon={ViewList}
      iconColor='primary'
      title={t('GenericFilterTemplate.GeneralData')}
      actions={
        <IconButton
          color='primary'
          variant='text'
          size='tiny'
          tooltip={t('Save')}
          onClick={handleSave}
          disabled={!isDirty || saving || !canWrite}
        >
          <Save />
        </IconButton>
      }
    >
      <Prompt when={isDirty && !saving} message={t('LeavingWithoutSaving')} />
      <FlexBox gap={2} alignItems='end'>
        <TextField
          fullWidth
          value={get(lens.name) || ''}
          onChange={event => set(lens.name)(event)}
          label={t('GenericFilterTemplate.Name')}
          error={!isValid(validation.name)}
          helperText={getErrors(validation.name)}
          disabled={saving || !canWrite}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={get(lens.isTemplate) || false}
              onChange={event => set(lens.isTemplate)(event.target.checked)}
              color='primary'
              disabled={saving || !canWrite}
            />
          }
          label={t('GenericFilterTemplate.IsTemplate')}
        />
      </FlexBox>
    </Card>
  );
};

GenericFilterTemplateGeneralDataComponent.propTypes = {
  id: PropTypes.number
};

export default GenericFilterTemplateGeneralDataComponent;
